import {EventDatasetCategory} from '#lit/types/datasets.js';
import {SupportedLocales} from '#lit/types/locale.js';

/**
 * Global window constants.
 */

// Uncomment to enable debugging features.
window.__DEBUG__ = false;

const debug = () => {
  window.__DEBUG__ = !window.__DEBUG__;
  const event = new CustomEvent('debug', {
    detail: {debugMode: window.__DEBUG__},
  });
  window.dispatchEvent(event);
  return window.__DEBUG__;
};

debug.tool = (value: Record<string, unknown>) => {
  Object.keys(value).forEach(key => {
    if (key === 'tool') {
      throw new Error('Cannot override tool method');
    }
  });
  window.addEventListener('debug', ({detail: {debugMode}}) => {
    if (debugMode) {
      Object.assign(debug, value);
    } else {
      Object.keys(value).forEach(key => {
        // @ts-expect-error - This is a hack to remove the key from the object.
        key in debug && delete debug[key];
      });
    }
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as any).debug = debug;

/**
 * Base unit for spacing in our styles.
 */
export const SPACING_UNIT = 12;

/**
 * General constants.
 */

export const ASSETS_PATH = '/assets';
export const IMAGES_PATH = '/assets/images';
export const LOCALE_DEFAULT = SupportedLocales.EN;

export enum Alignment {
  CENTER = 'center',
  LEFT = 'left',
  RIGHT = 'right',
}

export enum RootColor {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  GREEN = 'green',
  YELLOW = 'yellow',
  RED = 'red',
  PRIMARY_LIGHT = 'primary-light',
  SECONDARY_LIGHT = 'secondary-light',
  GREEN_LIGHT = 'green-light',
  YELLOW_LIGHT = 'yellow-light',
  RED_LIGHT = 'red-light',
}

export enum NeutralColor {
  BACKGROUND = 'background',
  HEADLINE = 'headline',
  BODY = 'body',
}

export const rootColorVarMap = new Map([
  [RootColor.PRIMARY, 'var(--fly-color-primary)'],
  [RootColor.SECONDARY, 'var(--fly-color-primary)'],
  [RootColor.GREEN, 'var(--fly-color-success)'],
  [RootColor.YELLOW, 'var(--fly-color-warning)'],
  [RootColor.RED, 'var(--fly-color-danger)'],
  [RootColor.PRIMARY_LIGHT, 'var(--fly-color-primary--light)'],
  [RootColor.SECONDARY_LIGHT, 'var(--fly-color-primary--light)'],
  [RootColor.GREEN_LIGHT, 'var(--fly-color-success--light)'],
  [RootColor.YELLOW_LIGHT, 'var(--fly-color-warning--light)'],
  [RootColor.RED_LIGHT, 'var(--fly-color-danger--light)'],
]);

export const rootBaseToLightMap = new Map([
  [RootColor.PRIMARY, RootColor.PRIMARY_LIGHT],
  [RootColor.SECONDARY, RootColor.SECONDARY_LIGHT],
  [RootColor.GREEN, RootColor.GREEN_LIGHT],
  [RootColor.YELLOW, RootColor.YELLOW_LIGHT],
  [RootColor.RED, RootColor.RED_LIGHT],
]);

// All possible audience statuses
export enum AudienceStatus {
  DRAFT = 'draft',
  PENDING_APPROVAL = 'pending_approval',
  IN_REVIEW = 'in_review',
  APPROVED = 'approved',
  EXPORTED = 'exported',
  EXPORTING = 'exporting',
  SCHEDULED = 'scheduled',
  ARCHIVED = 'archived',
}

export enum JourneyStatus {
  DRAFT = 'draft',
  SIMULATED = 'simulated',
  PENDING_REVIEW = 'pending-review',
  IN_REVIEW = 'in-review',
  APPROVED = 'approved',
  PUBLISHING = 'publishing',
  PUBLISHED = 'published',
  STOPPING = 'stopping',
  STOPPED = 'stopped',
  ARCHIVED = 'archived',
  ALL = 'all',
}

export enum StatusAbstraction {
  DRAFT = 'draft',
  IN_REVIEW = 'in-review',
  ACTIVE = 'active',
}

export const eventDatasetCategoryLabelMap: ReadonlyMap<
  EventDatasetCategory,
  string
> = new Map<EventDatasetCategory, string>([
  [EventDatasetCategory.WEB, 'Web'],
  [EventDatasetCategory.MOBILE_APP, 'Mobile App'],
  [EventDatasetCategory.EMAIL, 'E-mail'],
  [EventDatasetCategory.OTHER, 'Other'],
]);

export const audienceStatusLabelMap: Map<AudienceStatus, string> = new Map([
  [AudienceStatus.DRAFT, 'Draft'],
  [AudienceStatus.PENDING_APPROVAL, 'Pending Approval'],
  [AudienceStatus.IN_REVIEW, 'In-review'],
  [AudienceStatus.APPROVED, 'Approved'],
  [AudienceStatus.SCHEDULED, 'Scheduled Export'],
  [AudienceStatus.EXPORTED, 'Inactive Export'],
  [AudienceStatus.EXPORTING, 'Active Export'],
  [AudienceStatus.ARCHIVED, 'Archived'],
]);

export const journeyStatusLabelMap: Map<JourneyStatus, string> = new Map([
  [JourneyStatus.DRAFT, 'Draft'],
  [JourneyStatus.PUBLISHING, 'Publishing'],
  [JourneyStatus.PUBLISHED, 'Published'],
  [JourneyStatus.SIMULATED, 'Simulated'],
  [JourneyStatus.STOPPING, 'Stopping'],
  [JourneyStatus.STOPPED, 'Stopped'],
]);

export const audienceStatusAbstractionLabelMap: Map<StatusAbstraction, string> =
  new Map([
    [StatusAbstraction.DRAFT, 'Draft'],
    [StatusAbstraction.IN_REVIEW, 'In-review'],
    [StatusAbstraction.ACTIVE, 'Active'],
  ]);

export const abstractionToStatusesMap: Map<
  StatusAbstraction,
  AudienceStatus[]
> = new Map([
  [StatusAbstraction.DRAFT, [AudienceStatus.DRAFT]],
  [
    StatusAbstraction.IN_REVIEW,
    [AudienceStatus.PENDING_APPROVAL, AudienceStatus.IN_REVIEW],
  ],
  [
    StatusAbstraction.ACTIVE,
    [
      AudienceStatus.APPROVED,
      AudienceStatus.EXPORTED,
      AudienceStatus.EXPORTING,
    ],
  ],
]);

export enum RequestMethod {
  INFO = 'INFO',
  POST = 'POST',
  GET = 'GET',
  PUT = 'PUT',
  PATCH = 'PATCH',
  DELETE = 'DELETE',
}

export enum KeyCode {
  BACKSPACE = 8,
  TAB = 9,
  ENTER = 13,
  SHIFT = 6,
  CTRL = 17,
  ALT = 18,
  PAUSE = 19,
  CAPS_LOCK = 20,
  ESCAPE = 27,
  SPACE = 32,
  PAGE_UP = 33,
  PAGE_DOWN = 34,
  END = 35,
  HOME = 36,
  LEFT_ARROW = 37,
  UP_ARROW = 38,
  RIGHT_ARROW = 39,
  DOWN_ARROW = 40,
  INSERT = 45,
  DELETE = 46,
  KEY_0 = 48,
  KEY_1 = 49,
  KEY_2 = 50,
  KEY_3 = 51,
  KEY_4 = 52,
  KEY_5 = 53,
  KEY_6 = 54,
  KEY_7 = 55,
  KEY_8 = 56,
  KEY_9 = 57,
  KEY_A = 65,
  KEY_B = 66,
  KEY_C = 67,
  KEY_D = 68,
  KEY_E = 69,
  KEY_F = 70,
  KEY_G = 71,
  KEY_H = 72,
  KEY_I = 73,
  KEY_J = 74,
  KEY_K = 75,
  KEY_L = 76,
  KEY_M = 77,
  KEY_N = 78,
  KEY_O = 79,
  KEY_P = 80,
  KEY_Q = 81,
  KEY_R = 82,
  KEY_S = 83,
  KEY_T = 84,
  KEY_U = 85,
  KEY_V = 86,
  KEY_W = 87,
  KEY_X = 88,
  KEY_Y = 89,
  KEY_Z = 90,
  LEFT_META = 91,
  RIGHT_META = 92,
  SELECT = 93,
  NUMPAD_0 = 96,
  NUMPAD_1 = 97,
  NUMPAD_2 = 98,
  NUMPAD_3 = 99,
  NUMPAD_4 = 100,
  NUMPAD_5 = 101,
  NUMPAD_6 = 102,
  NUMPAD_7 = 103,
  NUMPAD_8 = 104,
  NUMPAD_9 = 105,
  MULTIPLY = 106,
  ADD = 107,
  SUBTRACT = 109,
  DECIMAL = 110,
  DIVIDE = 111,
  F1 = 112,
  F2 = 113,
  F3 = 114,
  F4 = 115,
  F5 = 116,
  F6 = 117,
  F7 = 118,
  F8 = 119,
  F9 = 120,
  F10 = 121,
  F11 = 122,
  F12 = 123,
  NUM_LOCK = 144,
  SCROLL_LOCK = 145,
  SEMICOLON = 186,
  EQUALS = 187,
  COMMA = 188,
  DASH = 189,
  PERIOD = 190,
  FORWARD_SLASH = 191,
  GRAVE_ACCENT = 192,
  OPEN_BRACKET = 219,
  BACK_SLASH = 220,
  CLOSE_BRACKET = 221,
  SINGLE_QUOTE = 222,
}

/**
 * Frontend-wide error code symbols.
 */
export enum FlyErrorCodes {
  /**
   * There was an error parsing the response from the server; usually means that
   * the frotnend expected JSON, but the server returned something else.
   */
  AB_SYNTAX_ERROR = 'AB_01',
}

/**
 * Labels to use per error code when rendering notifications.
 */
export const flyErrorLabels = new Map([
  [
    FlyErrorCodes.AB_SYNTAX_ERROR,
    'Error: Unable to process response from server.',
  ],
]);

export enum HTMLInputTypes {
  TEXT = 'text',
  NUMBER = 'number',
  DATETIME = 'datetime-local',
  DATE = 'date',
}

export enum BlobTypes {
  CSV = 'text/csv;charset=utf-8;',
}

/**
 * Enum of base event types.
 */
export enum ElementEventType {
  Change = 'change',
}

/**
 * Common time frames in milliseconds
 */
export const ONE_SECOND_MS = 1_000;
export const FIVE_SECOND_MS = 5 * ONE_SECOND_MS;
export const TEN_SECOND_MS = 10 * ONE_SECOND_MS;
export const THIRTY_SECOND_MS = 30 * ONE_SECOND_MS;
export const ONE_MINUTE_MS = 60 * ONE_SECOND_MS;
export const FIVE_MINUTE_MS = 5 * ONE_MINUTE_MS;
export const TEN_MINUTE_MS = 10 * ONE_MINUTE_MS;
